import { ErrorView1 } from '@epihub/web/src/components/ErrorView1';
import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconArrowLeft } from '@tabler/icons-react';
import { navigateTo } from '@epihub/web/src/routes/nav';
import { homeChatRoute } from '@krishanmadan/core/src/routes/frontendRoutes';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.danger.hex }}>{children}</h1>;
};

const notFoundStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  developer: {
    en: [
      { text: 'Why did the developer mass refresh?', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'To find something that was never there:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: "Perché lo sviluppatore ha aggiornato la pagina all'infinito?", Wrapper: BaseText },
      { ms: 1200 },
      { text: "Per trovare qualcosa che non c'era mai stato:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  wizard: {
    en: [
      { text: 'A wizard opened the Book of Infinite Wisdom and turned to the final page.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'It read:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: "Un mago aprì il Libro della Saggezza Infinita e andò all'ultima pagina.", Wrapper: BaseText },
      { ms: 800 },
      { text: "C'era scritto:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel contains every page ever written. A scholar searched for the one that mattered.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'The catalog read:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele contiene ogni pagina mai scritta. Uno studioso cercò quella che contava.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Il catalogo diceva:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  philosopher: {
    en: [
      { text: 'A philosopher asked: "On which page is the meaning of life written?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The universe answered:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: 'Un filosofo chiese: "Su quale pagina è scritto il senso della vita?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: "L'universo rispose:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  schrodinger: {
    en: [
      { text: 'Schrödinger finally opened the box. Inside was a note from the cat:', Wrapper: BaseText },
      { ms: 1200 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: "Schrödinger finalmente aprì la scatola. Dentro c'era un biglietto del gatto:", Wrapper: BaseText },
      { ms: 1200 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  transatlantic: {
    en: [
      {
        text: 'Europe opened the book of transatlantic friendship to find the page on US relations.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Trump had torn it out:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      {
        text: "L'Europa aprì il libro dell'amicizia transatlantica per trovare la pagina sui rapporti con gli USA.",
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: "Trump l'aveva strappata:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  brexit: {
    en: [
      { text: 'Britain left the EU and asked for the page on "having cake and eating it too."', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Brussels replied:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Gran Bretagna lasciò l\'UE e chiese la pagina su "volere la botte piena e la moglie ubriaca."',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Bruxelles rispose:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  climate: {
    en: [
      { text: 'World leaders gathered to find the page on climate action.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The oil lobby had redacted it:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: "I leader mondiali si riunirono per trovare la pagina sull'azione climatica.", Wrapper: BaseText },
      { ms: 1000 },
      { text: "La lobby del petrolio l'aveva censurata:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  healthcare: {
    en: [
      { text: 'An American searched for the page on affordable healthcare.', Wrapper: BaseText },
      { ms: 1200 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: 'Un americano cercò la pagina sulla sanità accessibile.', Wrapper: BaseText },
      { ms: 1200 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  billionaires: {
    en: [
      { text: 'The IRS opened their records to find the page on billionaire tax payments.', Wrapper: BaseText },
      { ms: 1200 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      {
        text: "L'Agenzia delle Entrate aprì i registri per trovare la pagina sulle tasse dei miliardari.",
        Wrapper: BaseText,
      },
      { ms: 1200 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  twitter: {
    en: [
      { text: 'A user searched X for the page on free speech.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Musk had put it behind a paywall:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: 'Un utente cercò su X la pagina sulla libertà di parola.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "Musk l'aveva messa a pagamento:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
  epstein: {
    en: [
      { text: 'The public searched for the page with the Epstein client list.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Trump had redacted it:', Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Page Not Found', Wrapper: Heading },
    ],
    it: [
      { text: 'Il pubblico cercò la pagina con la lista dei clienti di Epstein.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "Trump l'aveva censurata:", Wrapper: BaseText },
      { ms: 600 },
      { text: '404 Pagina Non Trovata', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(notFoundStories);
const randomStory = () => notFoundStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

export const GlobalNotFound = () => {
  const language = useGlobalStore((store) => store.settings.language);
  const [story] = React.useState(randomStory);

  const theme = useTheme();
  return (
    <Root>
      <Center>
        <ErrorView1
          mt={48}
          message={(
            <TextGenerateEffect
              words={story[language]}
            />
          )}
          cta={{
            node: (
              <ButtonWrapper>
                <HoverBorderGradient
                  containerClassName='rounded-full'
                  as='button'
                  className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2'
                  onClick={() => navigateTo(homeChatRoute)}
                  style={{ cursor: 'pointer' }}
                >
                  <IconArrowLeft color={theme.v0.col.danger.hex} />
                  <span>Home</span>
                </HoverBorderGradient>
              </ButtonWrapper>
            ),
          }}
        />
      </Center>
    </Root>
  );
};

const Root = styled.div`
  height: 75dvh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  :active {
    transform: scale(0.90);
  }
`;
